import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Paper,
  useMediaQuery,
  List,
  ListItem,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Badge,
  Chip,
} from '@mui/material'
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { CustomProvider, VStack, Calendar, HStack } from 'rsuite'
import 'rsuite/dist/rsuite.min.css'
import ruRU from 'rsuite/locales/ru_RU'
import api from '../../config/api'
import { API_URL } from '../../config/config'

const STORAGE_KEY = 'preorderQuantities'
const TTL_MS = 15 * 60 * 1000 // 15 минут

// Функция для загрузки из localStorage с проверкой TTL
const loadFromLocalStorage = () => {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return null

  try {
    const { timestamp, data } = JSON.parse(stored)
    if (Date.now() - timestamp < TTL_MS) {
      return data
    }
  } catch (e) {
    console.error('Ошибка чтения localStorage:', e)
  }

  return null
}

// Функция для сохранения в localStorage
const saveToLocalStorage = (data) => {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ timestamp: Date.now(), data }),
  )
}

const OrderConfirmationStep = ({
  quantities,
  availableDates,
  onBack,
  onSubmit,
  onQuantityChange,
  isLoading,
}) => {
  const [comment, setComment] = useState('')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Группируем товары по датам
  const groupedItems = availableDates.reduce((acc, dateGroup) => {
    const itemsWithQuantities = dateGroup.items
      .filter((item) => {
        const key = `${item.id}_${dateGroup.date}`
        return quantities[key] > 0
      })
      .map((item) => ({
        ...item,
        quantity: quantities[`${item.id}_${dateGroup.date}`],
        groupName: dateGroup.name,
        date: dateGroup.date,
        uniqueKey: `${item.id}_${dateGroup.date}`,
      }))

    if (itemsWithQuantities.length > 0) {
      acc.push({
        date: dateGroup.date,
        groupName: dateGroup.name,
        items: itemsWithQuantities,
      })
    }

    return acc
  }, [])

  const totalItems = Object.values(quantities).reduce(
    (sum, qty) => sum + qty,
    0,
  )

  const handleQuantityChange = (uniqueKey, newValue, stack = 1) => {
    const rounded = Math.round(newValue / stack) * stack
    const final = Math.max(rounded, 0)
    onQuantityChange(uniqueKey, final)
  }

  return (
    <Paper elevation={isMobile ? 0 : 1} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Подтверждение предзаказа
      </Typography>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Вы выбрали {totalItems} товар(ов) на {groupedItems.length} дат(у/ы)
      </Typography>

      <List>
        {groupedItems.map((group, groupIndex) => (
          <React.Fragment key={`group-${groupIndex}`}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              {group.date} - {group.groupName}
            </Typography>
            {group.items.map((item, itemIndex) => {
              const stack = item.category?.stack || 1
              return (
                <React.Fragment key={`item-${itemIndex}`}>
                  <ListItem sx={{ px: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <Box sx={{ width: 60, height: 60, mr: 2, flexShrink: 0 }}>
                        <img
                          src={`${API_URL}${item.image}`}
                          alt={item.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: theme.shape.borderRadius,
                          }}
                        />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1">{item.name}</Typography>
                        {item.category?.name && (
                          <Chip
                            label={item.category.name}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleQuantityChange(
                              item.uniqueKey,
                              item.quantity - stack,
                              stack,
                            )
                          }
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography
                          variant="body1"
                          sx={{ mx: 1, minWidth: 24, textAlign: 'center' }}
                        >
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleQuantityChange(
                              item.uniqueKey,
                              item.quantity + stack,
                              stack,
                            )
                          }
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </ListItem>
                  {itemIndex < group.items.length - 1 && <Divider />}
                </React.Fragment>
              )
            })}
          </React.Fragment>
        ))}
      </List>

      <TextField
        label="Комментарий к заказу"
        multiline
        rows={3}
        fullWidth
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        sx={{ mt: 3 }}
        placeholder="Укажите дополнительные пожелания к заказу"
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" onClick={onBack}>
          Назад к выбору
        </Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(comment)}
          disabled={isLoading || totalItems === 0}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Подтвердить заказ'}
        </Button>
      </Box>
    </Paper>
  )
}

const CalendarFeature = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [selectedDate, setSelectedDate] = useState(null)
  const [availableDates, setAvailableDates] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [quantities, setQuantities] = useState({})
  const [localQuantities, setLocalQuantities] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('API_FRONT/preorder/items/get-list/')
        setAvailableDates(response.data.available_dates_with_items || [])

        const initialQuantities = {}
        response.data.available_dates_with_items.forEach((dateGroup) => {
          dateGroup.items.forEach((item) => {
            const key = `${item.id}_${dateGroup.date}`
            initialQuantities[key] = 0
          })
        })

        // Загружаем из localStorage, если есть
        const savedQuantities = loadFromLocalStorage()
        if (savedQuantities) {
          setQuantities(savedQuantities)
          setLocalQuantities(savedQuantities)
        } else {
          setQuantities(initialQuantities)
          setLocalQuantities(initialQuantities)
        }
      } catch (err) {
        setError('Ошибка загрузки данных')
        console.error('Error fetching preorder items:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  const getItemsForSelectedDate = () => {
    if (!selectedDate || !availableDates.length) return []
    const day = selectedDate.getDate()
    const month = selectedDate.getMonth() + 1
    const year = selectedDate.getFullYear()
    const formattedDate = `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year}`
    return availableDates
      .filter((group) => group.date === formattedDate)
      .flatMap((group) =>
        group.items.map((item) => ({
          ...item,
          groupName: group.name,
          groupId: group.id,
          date: group.date,
          uniqueKey: `${item.id}_${group.date}`,
        })),
      )
  }

  const updateQuantity = (uniqueKey, newQuantity, stack = 1) => {
    const rounded = Math.round(newQuantity / stack) * stack
    const final = Math.max(rounded, 0)

    setQuantities((prev) => {
      const updated = { ...prev, [uniqueKey]: final }
      saveToLocalStorage(updated)
      return updated
    })

    setLocalQuantities((prev) => {
      const newValues = { ...prev }
      delete newValues[uniqueKey]
      return newValues
    })
  }

  const handleManualQuantityChange = (uniqueKey, value, stack = 1) => {
    const numericValue = parseInt(value) || 0
    if (numericValue < 0) return

    setLocalQuantities((prev) => ({
      ...prev,
      [uniqueKey]: numericValue,
    }))
  }

  const handleBlur = (uniqueKey, stack = 1) => {
    if (localQuantities[uniqueKey] !== undefined) {
      updateQuantity(uniqueKey, localQuantities[uniqueKey], stack)
    }
  }

  const handleSelectDate = (date) => {
    setSelectedDate(date)
  }

  const renderCell = (date) => {
    const day = date.getDate()
    const month = date.getMonth() + 1
    const year = date.getFullYear()
    const formattedDate = `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year}`

    const hasItems = availableDates.some(
      (group) => group.date === formattedDate,
    )

    return (
      <div style={{ position: 'relative' }}>
        {hasItems && (
          <div
            style={{
              position: 'relative',
              bottom: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: theme.palette.primary.main,
            }}
          />
        )}
      </div>
    )
  }

  const handleSubmitPreorder = async (comment) => {
    setIsSubmitting(true)
    try {
      const itemsToOrder = Object.entries(quantities)
        .filter(([_, qty]) => qty > 0)
        .map(([uniqueKey, quantity]) => {
          const [itemId, date] = uniqueKey.split('_')
          return { item_id: parseInt(itemId), quantity, date }
        })

      if (itemsToOrder.length === 0) {
        alert('Выберите хотя бы один товар')
        return
      }

      // await api.post('/API_FRONT/preorder/create/', {
      //   items: itemsToOrder,
      //   comment: comment
      // })

      // Очищаем корзину после успешного оформления
      setQuantities({})
      setLocalQuantities({})
      localStorage.removeItem(STORAGE_KEY)

      setOpenSnackbar(true)
    } catch (err) {
      console.error('Ошибка оформления предзаказа:', err)
      alert('Произошла ошибка при оформлении предзаказа')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setOpenSnackbar(false)
  }

  const handleClearAll = () => {
    setQuantities({})
    setLocalQuantities({})
    localStorage.removeItem(STORAGE_KEY)
  }

  const handleQuantityChangeInConfirmation = (uniqueKey, newValue) => {
    updateQuantity(uniqueKey, newValue)
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography color="error">{error}</Typography>
        <Button
          onClick={() => window.location.reload()}
          variant="outlined"
          sx={{ mt: 2 }}
        >
          Повторить попытку
        </Button>
      </Box>
    )
  }

  if (showConfirmation) {
    return (
      <OrderConfirmationStep
        quantities={quantities}
        availableDates={availableDates}
        onBack={() => setShowConfirmation(false)}
        onSubmit={handleSubmitPreorder}
        onQuantityChange={handleQuantityChangeInConfirmation}
        isLoading={isSubmitting}
      />
    )
  }

  const selectedItems = getItemsForSelectedDate()
  const hasSelectedItems = Object.values(quantities).some((qty) => qty > 0)

  return (
    <Paper elevation={isMobile ? 0 : 1} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Предзаказ товаров
      </Typography>

      <CustomProvider locale={ruRU}>
        <HStack
          spacing={10}
          style={{ height: 320 }}
          alignItems="flex-start"
          wrap
        >
          <VStack spacing={10}>
            <Calendar
              compact
              renderCell={renderCell}
              onSelect={handleSelectDate}
            />
          </VStack>
        </HStack>
      </CustomProvider>

      {selectedDate && (
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">
              Товары на {selectedDate.toLocaleDateString('ru-RU')}
            </Typography>
            {hasSelectedItems && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleClearAll}
                size="small"
              >
                Очистить все
              </Button>
            )}
          </Box>

          {selectedItems.length === 0 ? (
            <Typography variant="body1" color="text.secondary">
              Нет доступных товаров на выбранную дату
            </Typography>
          ) : (
            <List dense={isMobile}>
              {selectedItems.map((item) => {
                const stack = item.category?.stack || 1
                const displayValue =
                  localQuantities[item.uniqueKey] !== undefined
                    ? localQuantities[item.uniqueKey]
                    : quantities[item.uniqueKey] || 0

                return (
                  <React.Fragment key={item.uniqueKey}>
                    <ListItem
                      sx={{
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'flex-start',
                        px: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          width: '100%',
                          flexDirection: isMobile ? 'row' : 'row',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{ width: 80, height: 80, mr: 2, flexShrink: 0 }}
                        >
                          <img
                            src={`${API_URL}${item.image}`}
                            alt={item.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: theme.shape.borderRadius,
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            flexGrow: 1,
                            minWidth: 0,
                            mr: isMobile ? 0 : 2,
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 'bold',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {item.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip
                              label={item.groupName}
                              size="small"
                              variant="outlined"
                            />
                            {item.category?.name && (
                              <Chip
                                label={item.category.name}
                                size="small"
                                variant="filled"
                                color="primary"
                              />
                            )}
                          </Box>
                          {stack > 1 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Шаг: {stack} шт.
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          mt: isMobile ? 2 : 0,
                          pl: isMobile ? 9 : 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isMobile
                              ? 'space-between'
                              : 'flex-end',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newValue = Math.max(
                                  (quantities[item.uniqueKey] || 0) - stack,
                                  0,
                                )
                                updateQuantity(item.uniqueKey, newValue, stack)
                              }}
                              disabled={(quantities[item.uniqueKey] || 0) <= 0}
                            >
                              <RemoveIcon
                                fontSize={isMobile ? 'small' : 'medium'}
                              />
                            </IconButton>
                            <TextField
                              value={displayValue}
                              onChange={(e) =>
                                handleManualQuantityChange(
                                  item.uniqueKey,
                                  e.target.value,
                                  stack,
                                )
                              }
                              onBlur={() => handleBlur(item.uniqueKey, stack)}
                              type="number"
                              inputProps={{
                                min: 0,
                                step: stack,
                                style: {
                                  textAlign: 'center',
                                  width: isMobile ? '50px' : '60px',
                                  padding: isMobile ? '8px 4px' : '8px',
                                },
                              }}
                              variant="outlined"
                              size="small"
                              sx={{ mx: 0.5 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newValue =
                                  (quantities[item.uniqueKey] || 0) + stack
                                updateQuantity(item.uniqueKey, newValue, stack)
                              }}
                            >
                              <AddIcon
                                fontSize={isMobile ? 'small' : 'medium'}
                              />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                    <Divider component="li" sx={{ my: 1 }} />
                  </React.Fragment>
                )
              })}
            </List>
          )}

          {selectedItems.length > 0 && (
            <Box sx={{ mt: 3, textAlign: 'right' }}>
              <Button
                variant="contained"
                onClick={() => setShowConfirmation(true)}
                disabled={Object.values(quantities).every((qty) => qty === 0)}
              >
                Оформить предзаказ
              </Button>
            </Box>
          )}
        </Box>
      )}

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Предзаказ успешно создан!
        </Alert>
      </Snackbar>
    </Paper>
  )
}

export default CalendarFeature
